import { Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { toast } from "sonner";

import type { DocumentCategory } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUpdateDocument } from "../../hooks/use-update-document.ts";
import type { ConstructionDocument } from "../../types.ts";
import {
	DOCUMENT_CATEGORIES,
	documentCategoryLabel,
	isDocumentCategory,
} from "../../utils/categories.ts";
import { documentErrorMessage } from "../../utils/errors.ts";

export const DOCUMENT_TITLE_MAX_LENGTH = 200;

interface EditDocumentDialogProps {
	constructionId: string;
	document: ConstructionDocument | undefined;
	onOpenChange: (open: boolean) => void;
}

export function EditDocumentDialog({
	constructionId,
	document,
	onOpenChange,
}: EditDocumentDialogProps) {
	const ids = useId();
	const updateDocument = useUpdateDocument(constructionId);
	const [title, setTitle] = useState("");
	const [category, setCategory] = useState<DocumentCategory>("other");
	const [clientVisible, setClientVisible] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!document) return;
		setTitle(document.title);
		setCategory(document.category);
		setClientVisible(document.clientVisible);
		setError(null);
	}, [document]);

	const trimmed = title.trim();
	const titleError = trimmed ? null : "Informe um título.";

	async function submit(event: FormEvent) {
		event.preventDefault();
		if (!document || titleError) return;
		setError(null);
		try {
			await updateDocument.mutateAsync({
				documentId: document.id,
				input: { title: trimmed, category, clientVisible },
			});
			toast.success("Documento atualizado.");
			onOpenChange(false);
		} catch (err) {
			setError(
				documentErrorMessage(err, "Não foi possível atualizar o documento."),
			);
		}
	}

	return (
		<Dialog open={!!document} onOpenChange={onOpenChange}>
			<DialogContent className="document-edit">
				<form className="document-edit__form" onSubmit={submit}>
					<DialogHeader>
						<DialogTitle>Editar documento</DialogTitle>
						<DialogDescription className="document-edit__file-name">
							{document?.fileName}
						</DialogDescription>
					</DialogHeader>
					<div className="document-edit__field">
						<Label htmlFor={`${ids}-title`}>Título</Label>
						<Input
							id={`${ids}-title`}
							value={title}
							maxLength={DOCUMENT_TITLE_MAX_LENGTH}
							aria-invalid={!!titleError}
							onChange={(event) => setTitle(event.target.value)}
						/>
					</div>
					<div className="document-edit__field">
						<Label htmlFor={`${ids}-category`}>Categoria</Label>
						<Select
							value={category}
							onValueChange={(next) => {
								if (isDocumentCategory(next)) setCategory(next);
							}}
						>
							<SelectTrigger
								id={`${ids}-category`}
								className="document-edit__category"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{DOCUMENT_CATEGORIES.map((option) => (
									<SelectItem key={option} value={option}>
										{documentCategoryLabel(option)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="document-edit__switch">
						<Switch
							id={`${ids}-client-visible`}
							checked={clientVisible}
							onCheckedChange={setClientVisible}
						/>
						<Label htmlFor={`${ids}-client-visible`}>
							Visível para o cliente
						</Label>
					</div>
					{error && (
						<p role="alert" className="form-error-alert">
							{error}
						</p>
					)}
					<DialogFooter>
						<Button
							type="button"
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={updateDocument.isPending}
						>
							Cancelar
						</Button>
						<Button
							type="submit"
							disabled={!!titleError || updateDocument.isPending}
						>
							{updateDocument.isPending && (
								<Loader2 className="document-edit__spinner" />
							)}
							Salvar
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
